// ============================================
// Therapist Layout (PWA)
// ============================================

import React from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { collection, query, where, getDocs, onSnapshot, doc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Event, EventTherapist } from '../../types';
import InstallPrompt from '../../components/InstallPrompt';

// SVG Icons for cleaner look
const HomeIcon = ({ active }: { active: boolean }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" fill={active ? 'currentColor' : 'none'} />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);


const ProfileIcon = ({ active }: { active: boolean }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="4" fill={active ? 'currentColor' : 'none'} />
    <path d="M20 21a8 8 0 1 0-16 0" fill={active ? 'currentColor' : 'none'} />
  </svg>
);

const LogoutIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const AdminIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 4.5a2.5 2.5 0 0 0-4.96-.46 2.5 2.5 0 0 0-1.98 3 2.5 2.5 0 0 0 1.32 4.24 3 3 0 0 0 .34 5.58 2.5 2.5 0 0 0 2.96 3.08A2.5 2.5 0 0 0 12 21.5a2.5 2.5 0 0 0 2.32-1.56 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0 1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 12 4.5" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

export default function TherapistLayout() {
  const { user, roles, hasMultipleRoles, loading, signOut, switchRole, impersonation, stopImpersonation } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [therapistPhoto, setTherapistPhoto] = React.useState<string | null>(null);
  const [therapistStatus, setTherapistStatus] = React.useState<string | null>(null);

  // Status colors: green for on_duty, blue for break, orange for off_duty/checked out
  const getStatusColor = (status: string | null) => {
    switch (status) {
      case 'on_duty': return '#22c55e'; // green
      case 'break': return '#3b82f6'; // blue
      case 'off_duty': return '#f59e0b'; // orange
      default: return '#6b7280'; // gray
    }
  };

  React.useEffect(() => {
    if (!loading && !user) {
      navigate('/login', { state: { from: location }, replace: true });
    }
  }, [user, loading, navigate, location]);

  // Fetch therapist's photo and status from their current event membership
  React.useEffect(() => {
    if (!user) return;

    const findTherapistData = async () => {
      try {
        // Find live events where user is a therapist
        const liveEventsQuery = query(collection(db, 'events'), where('status', '==', 'live'));
        const eventsSnap = await getDocs(liveEventsQuery);

        for (const eventDoc of eventsSnap.docs) {
          try {
            const therapistRef = doc(db, 'events', eventDoc.id, 'therapists', user.uid);
            // Subscribe to therapist document for real-time updates
            const unsubscribe = onSnapshot(therapistRef, (therapistSnap) => {
              if (therapistSnap.exists()) {
                const data = therapistSnap.data() as EventTherapist;
                if (data.photoUrl && data.photoStatus === 'approved') {
                  setTherapistPhoto(data.thumbnailUrl || data.photoUrl);
                }
                setTherapistStatus(data.dutyStatus || null);
              }
            });
            // Only subscribe to first found event
            return () => unsubscribe();
          } catch {
            // No access to this event's therapist data
          }
        }
      } catch (err) {
        console.error('Error finding therapist data:', err);
      }
    };

    findTherapistData();
  }, [user]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const handleSwitchToAdmin = async () => {
    try {
      const adminRole = roles.find(r => r === 'superadmin' || r === 'admin');
      if (adminRole) {
        await switchRole(adminRole);
        navigate('/admin');
      }
    } catch (err) {
      console.error('Failed to switch role:', err);
    }
  };

  const canSwitchToAdmin = hasMultipleRoles && (roles.includes('admin') || roles.includes('superadmin'));

  const handleStopImpersonation = async () => {
    await stopImpersonation();
    window.location.href = '/login';
  };

  if (loading) {
    return (
      <div className="therapist-layout loading-screen">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const isHome = location.pathname === '/t';
  const isProfile = location.pathname === '/t/profile';

  return (
    <div className="therapist-layout" style={{ paddingTop: impersonation ? '48px' : 0 }}>
      {/* Impersonation Banner */}
      {impersonation && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          backgroundColor: '#7c3aed',
          color: '#fff',
          padding: '0.5rem 1rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: '0.875rem',
          height: '48px',
          boxSizing: 'border-box',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '1rem' }}>👁️</span>
            <span>
              Viewing as <strong>{impersonation.targetDisplayName || impersonation.targetEmail || 'User'}</strong>
            </span>
          </div>
          <button
            onClick={handleStopImpersonation}
            style={{
              backgroundColor: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '4px',
              color: '#fff',
              padding: '0.375rem 0.75rem',
              cursor: 'pointer',
              fontSize: '0.75rem',
              fontWeight: 600,
            }}
          >
            Stop Viewing
          </button>
        </div>
      )}

      <main className="therapist-main" style={{ paddingTop: '1rem' }}>
        <Outlet />
      </main>

      <nav className="therapist-nav-bottom" style={{
        display: 'flex',
        justifyContent: 'space-around',
        alignItems: 'center',
        padding: '0.5rem 0 calc(0.5rem + env(safe-area-inset-bottom))',
        backgroundColor: 'var(--color-bg-secondary)',
        borderTop: '1px solid var(--color-border)',
      }}>
        <Link
          to="/t"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.25rem',
            color: isHome ? 'var(--color-primary)' : 'var(--color-text-secondary)',
            textDecoration: 'none',
            padding: '0.5rem 1rem',
            borderRadius: '8px',
            backgroundColor: isHome ? 'rgba(34, 197, 94, 0.1)' : 'transparent',
            fontWeight: isHome ? 600 : 400,
          }}
        >
          <HomeIcon active={isHome} />
          <span style={{ fontSize: '0.7rem' }}>Home</span>
        </Link>

        <Link
          to="/t/profile"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.25rem',
            color: isProfile ? 'var(--color-primary)' : 'var(--color-text-secondary)',
            textDecoration: 'none',
            padding: '0.5rem 1rem',
          }}
        >
          {therapistPhoto ? (
            <img
              src={therapistPhoto}
              alt="Profile"
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                objectFit: 'cover',
                border: `2px solid ${getStatusColor(therapistStatus)}`,
              }}
            />
          ) : (
            <ProfileIcon active={isProfile} />
          )}
          <span style={{ fontSize: '0.7rem' }}>Profile</span>
        </Link>

        {canSwitchToAdmin && (
          <button
            onClick={handleSwitchToAdmin}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.25rem',
              color: 'var(--color-text-secondary)',
              background: 'none',
              border: 'none',
              padding: '0.5rem 1rem',
              cursor: 'pointer',
            }}
          >
            <AdminIcon />
            <span style={{ fontSize: '0.7rem' }}>Admin</span>
          </button>
        )}

        <button
          onClick={handleSignOut}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.25rem',
            color: 'var(--color-text-secondary)',
            background: 'none',
            border: 'none',
            padding: '0.5rem 1rem',
            cursor: 'pointer',
          }}
        >
          <LogoutIcon />
          <span style={{ fontSize: '0.7rem' }}>Sign Out</span>
        </button>
      </nav>

      <InstallPrompt variant="compact" />
    </div>
  );
}
